import { deDE, enUS } from '@mui/material/locale';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { deDE as deDEGrid, enUS as enUSGrid } from '@mui/x-data-grid-pro/locales';
import deepmerge from 'deepmerge';
import { forwardRef, useContext } from 'react';
import { Link as RouterLink } from 'react-router';

import { ApplicationContext } from '../../../../shared/source/context/context';
import { defaultTheme } from '../../../../shared/source/customization/customization-response';

import type { LanguageConfig } from '../../../../shared/source/internationalization';
import type { Localization } from '@mui/material/locale';
import type { ThemeOptions } from '@mui/material/styles';
import type { FunctionComponent, PropsWithChildren } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router';

const LinkBehavior = forwardRef<
    HTMLAnchorElement,
    Omit<RouterLinkProps, 'to'> & { readonly href: RouterLinkProps['to'] }
>((props, ref) => {
    const { href, ...other } = props;

    return <RouterLink ref={ref} to={href} {...other} />;
});

const LocalizationMap: Record<LanguageConfig['code'], Localization> = {
    'de-DE': deDE,
    'en-US': enUS,
};

const GridLocalizationMap: Record<LanguageConfig['code'], typeof enUSGrid> = {
    'de-DE': deDEGrid,
    'en-US': enUSGrid,
};

export const CustomThemeProvider: FunctionComponent<PropsWithChildren> = (props) => {
    const { applicationState } = useContext(ApplicationContext);
    const muiLocalization = LocalizationMap[applicationState.language.code];
    const muiGridLocalization = GridLocalizationMap[applicationState.language.code];

    const uiConfigurationTheme = applicationState.customization
        .get('uiConfiguration')
        .get('theme')
        .unwrapOr(defaultTheme) as ThemeOptions;

    const theme = createTheme(
        deepmerge(uiConfigurationTheme, {
            components: {
                MuiButtonBase: { defaultProps: { LinkComponent: LinkBehavior } },
                MuiLink: { defaultProps: { component: LinkBehavior } },
            },
        }),
        muiLocalization,
        muiGridLocalization,
    );

    return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
};

export const Shade = {
    p12: 0.12,
    p30: 0.3,
    p4: 0.04,
    p60: 0.6,
    p8: 0.08,
} as const;
